import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaChalkboardTeacher, FaArrowRight } from 'react-icons/fa';

const CommonLogin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('user');
  const [isLoading, setIsLoading] = useState(false);

  const handleUserLogin = () => {
    setIsLoading(true);
    // Simulate navigation delay
    setTimeout(() => {
      navigate('/user/login');
      setIsLoading(false);
    }, 800);
  };

  const handleMentorLogin = () => {
    setIsLoading(true);
    // Simulate navigation delay
    setTimeout(() => {
      navigate('/mentor/login');
      setIsLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-white to-violet-100">
      {/* Desktop View - Split Screen */}
      <div className="hidden lg:flex min-h-screen">
        {/* Left Side - User Login Box */}
        <div className="w-1/2 bg-white flex items-center justify-center p-12">
          <div className="max-w-md w-full">
            <div className="text-center">
              {/* User Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-violet-100 rounded-full mb-4">
                <FaUser className="text-3xl text-violet-600" />
              </div>

              {/* Title */}
              <h2 className="text-3xl font-bold text-violet-800 mb-4">User Login</h2>
              <p className="text-violet-600 mb-8 leading-relaxed">
                Continue your learning journey with personalized courses and expert mentorship
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-2 bg-violet-50 rounded-xl border">
                  <div className="size-6 bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-violet-700">Personalized Learning Paths</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-violet-50 rounded-xl border">
                  <div className="size-6 bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-violet-700">1-on-1 Mentor Sessions</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-violet-50 rounded-xl border">
                  <div className="size-6 bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-violet-700">Progress Tracking & Analytics</span>
                </div>
              </div>

              {/* Login Button */}
              <button
                onClick={handleUserLogin}
                className="w-full bg-violet-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-violet-700 transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                <span>Start Your Learning Journey</span>
                <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300" />
              </button>

              <div className="text-center mt-6">
                <p className="text-gray-500 text-sm">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>

            </div>
          </div>
        </div>

        {/* Right Side - Mentor Login */}
        <div className="w-1/2 bg-gradient-to-br from-violet-600 to-violet-800 flex items-center justify-center p-12">
          <div className="max-w-md w-full">
            <div className="text-center text-white">
              {/* Mentor Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4">
                <FaChalkboardTeacher className="text-3xl text-white" />
              </div>

              {/* Title */}
              <h2 className="text-3xl font-bold text-white mb-4">Mentor Portal</h2>
              <p className="text-violet-100 mb-8 leading-relaxed">
                Share your expertise and guide the next generation while earning rewards
              </p>

              {/* Features List */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                  <div className="size-6 bg-gradient-to-r from-white/30 to-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-white">Flexible Scheduling</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                  <div className="size-6 bg-gradient-to-r from-white/30 to-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-white">Competitive Earnings</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                  <div className="size-6 bg-gradient-to-r from-white/30 to-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-white">Global Impact</span>
                </div>
              </div>

              {/* Login Button */}
              <button
                onClick={handleMentorLogin}
                className="w-full bg-white text-violet-600 font-semibold py-3 px-6 rounded-lg hover:bg-violet-50 transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                <span>Mentor Login</span>
                <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300" />
              </button>

              <div className="text-center mt-6">
                <p className="text-white text-sm">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Mobile View - Tab Switch */}
      <div className="lg:hidden h-screen flex flex-col">
        {/* Tab Navigation */}
        <div className={`relative bg-gradient-to-b ${activeTab == 'user' ? 'from-violet-50 to-violet-100' : 'from-violet-800 to-violet-700'} p-2`}>
          <div className={`flex bg-white rounded-xl border ${activeTab == 'user' ? 'border-violet-500' : 'border-white'} overflow-hidden`}>
            <button
              onClick={() => setActiveTab('user')}
              className={`flex-1 py-4 px-4 font-semibold text-center transition-all duration-300 relative ${activeTab === 'user'
                ? 'bg-gradient-to-r from-violet-600 to-violet-700 text-white '
                : 'text-violet-700 '
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <div className={`p-1.5 rounded-full ${activeTab === 'user' ? 'bg-white/20' : 'bg-violet-100'
                  }`}>
                  <FaUser className={`text-sm ${activeTab === 'user' ? 'text-white' : 'text-violet-600'
                    }`} />
                </div>
                <span className="text-sm font-bold">User Login</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('mentor')}
              className={`flex-1 py-4 px-4 font-semibold text-center transition-all duration-300 relative ${activeTab === 'mentor'
                ? 'bg-gradient-to-r from-violet-600 to-violet-700 text-white '
                : 'text-violet-700'
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <div className={`p-1.5 rounded-full ${activeTab === 'mentor' ? 'bg-white/20' : 'bg-violet-100'
                  }`}>
                  <FaChalkboardTeacher className={`text-sm ${activeTab === 'mentor' ? 'text-white' : 'text-violet-600'
                    }`} />
                </div>
                <span className="text-sm font-bold">Mentor Portal</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 h-full">
          {activeTab === 'user' ? (
            /* User Tab Content */
            <div className="flex items-center justify-center h-full min-h-full p-6 bg-gradient-to-b from-violet-100 to-violet-200">
              <div className="max-w-lg w-full">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-violet-100">
                  {/* User Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <FaUser className="text-2xl text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-violet-800 mb-2">Welcome Back!</h2>
                    <p className="text-violet-600 text-sm leading-relaxed">
                      Continue your learning journey with personalized courses and expert mentorship
                    </p>
                  </div>

                  {/* Features Cards */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 p-2 bg-violet-50/50 rounded-xl border">
                      <div className="size-6 bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium text-violet-700">Personalized Learning Paths</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-violet-50/50 rounded-xl border">
                      <div className="size-6 bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium text-violet-700">1-on-1 Mentor Sessions</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-violet-50/50 rounded-xl border">
                      <div className="size-6 bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium text-violet-700">Progress Tracking & Analytics</span>
                    </div>
                  </div>

                  {/* Login Button */}
                  <button
                    onClick={handleUserLogin}
                    className="w-full bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 group shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <span>Start Learning Journey</span>
                    <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Mentor Tab Content */
            <div className="flex items-center justify-center h-full min-h-full p-6 bg-gradient-to-t from-violet-800 to-violet-700">
              <div className="max-w-lg w-full">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/20">
                  {/* Mentor Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      <div className="w-20 h-20 bg-gradient-to-br from-white/20 to-white/10 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm border border-white/30">
                        <FaChalkboardTeacher className="text-2xl text-white" />
                      </div>

                    </div>
                  </div>

                  {/* Title */}
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-white mb-2">Mentor Portal</h2>
                    <p className="text-violet-100 text-sm leading-relaxed">
                      Share your expertise and guide the next generation while earning rewards
                    </p>
                  </div>

                  {/* Features Cards */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 p-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                      <div className="size-6 bg-gradient-to-r from-white/30 to-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium text-white">Flexible Scheduling</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                      <div className="size-6 bg-gradient-to-r from-white/30 to-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium text-white">Competitive Earnings</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                      <div className="size-6 bg-gradient-to-r from-white/30 to-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium text-white">Global Impact</span>
                    </div>
                  </div>

                  {/* Login Button */}
                  <button
                    onClick={handleMentorLogin}
                    className="w-full bg-white/90 hover:bg-white text-violet-700 hover:text-violet-800 font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-3 group shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 backdrop-blur-sm"
                  >
                    <span>Access Mentor Portal</span>
                    <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white font-medium text-lg">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommonLogin;